import { AxiosError } from "axios";

/**
 * Raised when a refresh cannot even be attempted — there is no refresh token id to present.
 * It carries no HTTP status, so it needs its own signal or the caller cannot tell it apart
 * from a transient network failure.
 */
export class SessionUnrecoverableError extends Error {
  /**
   * Which terminal case this is. Both are equally terminal, so `isSessionDead` treats them
   * identically and no recovery behaviour changes — they are told apart only for telemetry, where
   * conflating them would be actively misleading. `no-refresh-id` is the expected steady state of
   * every embed and public dashboard; `malformed-response` means the server answered 2xx with
   * something that was not a token (AB#10507). A rising count of the first is normal traffic. A
   * rising count of the second is an outage.
   */
  public readonly code: "no-refresh-id" | "malformed-response";

  constructor(
    message: string,
    code: "no-refresh-id" | "malformed-response" = "no-refresh-id"
  ) {
    super(message);
    this.name = "SessionUnrecoverableError";
    this.code = code;
    // Required for `instanceof` to work when targeting ES5.
    Object.setPrototypeOf(this, SessionUnrecoverableError.prototype);
  }
}

/**
 * True when the server said this browser holds a refresh credential it cannot use.
 *
 * The distinction matters because it is invisible from the client. A stranded admin and an anonymous
 * visitor on a public page both see a bodyless 401 from `/account/refresh`; only the server knows
 * that the first one presented an HttpOnly refresh secret and the second presented nothing. Without
 * this code the client would have to redirect on any refused refresh, which bounces public-page
 * visitors to an admin login (AB#9773).
 *
 * Tolerant of every shape the error can actually take -- no `response`, no `data`, `data` as a
 * string (an IIS or proxy HTML error page) or an array -- because this runs on a failure path,
 * and a predicate that throws there would replace a session loss with an unhandled rejection.
 */
export function isRefreshCredentialUnusable(error: unknown): boolean {
  const data = (error as { response?: { data?: unknown } })?.response?.data;

  return (
    typeof data === "object" &&
    data !== null &&
    !Array.isArray(data) &&
    (data as { code?: unknown }).code === "refresh-credential-unusable"
  );
}

/**
 * Single definition of "the session is over, stop retrying and send the user to log in".
 *
 * `/account/refresh` answers 401 when the refresh secret or token id is missing, unusable, or
 * replayed. It no longer answers 400 at all: the one branch that did was the case where a browser
 * holds a refresh secret with no token id, which is not a malformed request but a dead session, and
 * answering 400 stranded admins on a page that never prompted a login (AB#10737). 400 stays excluded
 * here anyway — if some future branch starts returning it, it will mean a genuinely malformed
 * request, and logging users out over a client bug is still the wrong trade.
 *
 * A refresh that failed without any HTTP status is also terminal when it could never have
 * succeeded. That now means one thing only: a session holding an exchanged access token and an
 * empty refresh id -- embed and public-dashboard pages -- which the server's signed-in-session
 * fallback can only ever refuse. A merely *absent* id is not terminal; that tab asks the server,
 * because only the server knows whether a live family is waiting for it.
 */
export function isSessionDead(error: unknown): boolean {
  if (error instanceof SessionUnrecoverableError) {
    return true;
  }

  // The brand, because `instanceof` does not survive a bundle boundary. TokenRefreshService is a
  // window singleton owned by whichever bundle evaluated first, but this module is duplicated into
  // every bundle - so an error thrown by the singleton is an instance of THAT bundle's class and
  // fails the check above in any other. The status test then finds no response.status and returns
  // false, and a genuinely dead session is treated as transient: no clearAuth, no redirect. On a
  // page loading five bundles that is the normal case, not an edge one (AB#10738).
  //
  // `name`, never `constructor.name`: terser mangles class names in the production build, so a
  // constructor-name check passes locally and returns false in every deployed bundle - the same
  // invisible-in-dev failure this is fixing.
  if ((error as { name?: unknown } | null)?.name === "SessionUnrecoverableError") {
    return true;
  }

  const status = (error as AxiosError)?.response?.status;

  return status === 401 || status === 403;
}